import React, { useState } from 'react';
import { Theme } from '../../../../Enums/Theme.enum';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import styles from './IssuedLaboratoryRequestsListItem.module.scss';
import { prepareDocumentationFileForMediaViewer } from '../../../Ecard/Services/eCardFile.service';
import { useAppContext } from '../../../../Services/AppContext';
import {
	LaboratoryRequisitionResult,
	useAddLaboratoryRequisitionToPatientMutation,
	useDeleteLaboratoryRequisitionResultMutation,
	useDuplicateLaboratoryRequisitionResultMutation,
	useLazyLaboratoryRequisitionResultQuery,
} from '../../../../graphql/graphql';
import LinkButton from '../../../../Components/Button/LinkButton';
import Text from '../../../../Components/Base/Text/Text';
import usePatientConsent from '../../../../Components/PatientProfile/Hooks/usePatientConsent';
import BaseModal from '../../../Base/Components/BaseModal/BaseModal';
import PatientNoConsentProvided from '../../../Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import FileSignedBadge from '../../../Base/Components/FileSignedBadge/FileSignedBadge';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import SavingSpinner from '../../../../Components/Base/Loading/SavingSpinner';
import useSocket from '../../../Base/Hooks/useSocket';
import { Socket } from '../../../Base/Enums/Socket.enum';
import { DocumentStatus } from '../../../Base/Enums/DocumentStatus.enum';
import useApiClient from '../../../Base/Hooks/useApiClient';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import IssuedLaboratoryRequestLogModal from '../IssuedLaboratoryRequestLog/IssuedLaboratoryRequestLogModal';

interface Props {
	patientId: string;
	item: LaboratoryRequisitionResult;
	onUpdate: (item: LaboratoryRequisitionResult) => void;
	refreshList: () => void;
}

const IssuedLaboratoryRequestsListItem: React.FC<Props> = ({
	item,
	patientId,
	onUpdate,
	refreshList,
}) => {
	const {
		isOpen: isLogOpen,
		onOpen: onOpenLog,
		onClose: onCloseLog,
	} = useDisclose();
	const [isFileOpen, setIsFileOpen] = useState(false);
	const [isNoConsentProvidedOpen, setIsNoConsentProvidedOpen] = useState(false);
	const { isConsentProvided } = usePatientConsent(patientId);
	const { socketUrl } = useAppContext();
	const { displayGQLError } = useApiClient();
	const [sendToPatient] = useAddLaboratoryRequisitionToPatientMutation();
	const [getLaboratoryRequisitionResult] =
		useLazyLaboratoryRequisitionResultQuery();
	const [deleteLaboratoryRequisitionResult] =
		useDeleteLaboratoryRequisitionResultMutation();
	const [duplicateLaboratoryRequisitionResult] =
		useDuplicateLaboratoryRequisitionResultMutation();

	const handleDelete = async () => {
		const response = await deleteLaboratoryRequisitionResult({
			input: { id: item.id },
		}).unwrap();
		if (response.deleteLaboratoryRequisitionResult?.status) {
			refreshList();
		}
		displayGQLError(response);
	};

	const handleDuplicate = async () => {
		const response = await duplicateLaboratoryRequisitionResult({
			input: { id: item.id },
		}).unwrap();
		if (response.duplicateLaboratoryRequisitionResult) {
			refreshList();
		}
		displayGQLError(response);
	};

	const refetchItem = async () => {
		const data = await getLaboratoryRequisitionResult({
			where: { id: item.id },
		}).unwrap();
		if (data.laboratoryRequisitionResult) {
			onUpdate(data.laboratoryRequisitionResult as LaboratoryRequisitionResult);
		}
	};

	const handleSendToPatientsECard = async () => {
		if (isConsentProvided) {
			await sendToPatient({
				input: { laboratoryRequisitionResultId: item.id },
			});
			await refetchItem();
		} else {
			setIsNoConsentProvidedOpen(true);
		}
	};

	const onSocket = async (data: {
		laboratoryRequisitionResultId: string;
		status: DocumentStatus;
	}) => {
		if (
			data.laboratoryRequisitionResultId === item.id &&
			data.status === DocumentStatus.DOCUMENT_CREATED
		) {
			await refetchItem();
		}
	};

	useSocket(Socket.LABORATORY_REQUISITION, data => onSocket(data));

	const isFileProcessing =
		item.laboratoryExaminationRequest?.file?.processing ?? true;

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.date}>
					<FormattedMomentDate
						date={item.createdAt}
						format={DateFormat.DATE_TIME}
					/>
				</div>
				<div className={styles.content}>
					<div className={styles.title}>{item.requisition?.name}</div>
					<div className={styles.subTitle}>
						{item.requisition?.laboratory?.name}
					</div>
				</div>
				<div className={styles.controls}>
					{isFileProcessing ? (
						<SavingSpinner tx='base.documentIsProcessing' />
					) : (
						<>
							{item.laboratoryExaminationRequest?.file?.signed && (
								<FileSignedBadge />
							)}
							{item.laboratoryExaminationRequestPatientId ? (
								<div className={styles.inPatientsECard}>
									<img
										className={styles.check}
										src='/dist/icons/v2/check-green.svg'
										alt='green check'
									/>{' '}
									<Text
										inline
										tx='component.issuedLaboratoryRequestsListItem.isSendToPatient'
									/>
								</div>
							) : (
								<LinkButton
									title='component.issuedLaboratoryRequestsListItem.sendToPatientECard'
									theme={Theme.BLUE}
									onClick={handleSendToPatientsECard}
								/>
							)}
							<LinkButton
								title='component.issuedLaboratoryRequestsListItem.log'
								theme={Theme.BLUE}
								onClick={onOpenLog}
							/>
							<LinkButton
								onClick={handleDuplicate}
								title='component.issuedLaboratoryRequestsListItem.duplicate'
								theme={Theme.BLUE}
								firstUpper
							/>
							<RemoveButton onClick={handleDelete} firstUpper />
							<LinkButton
								onClick={() => setIsFileOpen(true)}
								title='base.show'
								theme={Theme.BLUE}
								firstUpper
							/>
						</>
					)}
				</div>
			</div>
			{isFileOpen && item.laboratoryExaminationRequest && (
				<MediaViewerModal
					opened
					onClose={() => setIsFileOpen(false)}
					file={prepareDocumentationFileForMediaViewer(
						item.laboratoryExaminationRequest,
						socketUrl,
					)}
					isSignable={item.laboratoryExaminationRequest.canBeSigned || false}
				/>
			)}
			{isNoConsentProvidedOpen && (
				<BaseModal
					title='component.issuedLaboratoryRequestsListItem.cantBeSendToPatient'
					isOpen
					onClose={() => setIsNoConsentProvidedOpen(false)}>
					<PatientNoConsentProvided patientId={patientId} />
				</BaseModal>
			)}
			{isLogOpen && (
				<IssuedLaboratoryRequestLogModal
					requestId={item.id}
					isOpen
					onClose={onCloseLog}
				/>
			)}
		</>
	);
};

export default IssuedLaboratoryRequestsListItem;
